import React, { useState } from 'react';
import referenceData from '../data/referenceNotes.json';
import { formatTime } from '../utils/timeUtils';

// Function to extract YouTube video ID and create proper embed URL
const getYouTubeEmbedUrl = (url) => {
  try {
    // Extract video ID using regex
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    // If match found, return proper embed URL
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    
    // Fallback: try simple replacement if regex fails
    if (url.includes('watch?v=')) {
      return url.replace('watch?v=', 'embed/');
    }
    
    // If all else fails, return original URL
    return url;
  } catch (error) {
    console.error('Error formatting YouTube URL:', error);
    return url;
  }
};

const ReferenceOverlay = ({ currentTime, inPanel = false, videoControls }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  
  // Buffer time (in seconds) to keep a note visible after its timestamp
  const bufferTime = 5;
  
  // Find the reference note at the current timestamp
  const referenceNote = referenceData.find(note => {
    return currentTime >= note.seconds && currentTime < note.seconds + bufferTime;
  });
  
  if (!referenceNote) {
    return inPanel ? (
      <p className="text-gray-400 italic">No references at this timestamp.</p>
    ) : null;
  }
  
  const handleLinkClick = (note) => {
    if (videoControls) {
      videoControls.pause();
    }
    setModalContent(note);
    setShowModal(true);
  };
  
  const closeModal = () => {
    setShowModal(false);
    setModalContent(null);
    if (videoControls) {
      videoControls.play();
    }
  };
  
  // Render the reference note
  return (
    <>
      <div className={inPanel ? "" : "absolute bottom-16 left-0 right-0 bg-black bg-opacity-70 p-2 text-white"}>
        <div className="flex items-start mb-2">
          <div className="flex-1">
            <p className="text-sm text-gray-300">{formatTime(referenceNote.seconds)}</p>
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-md font-semibold">{referenceNote.title}</h4>
                <p className="text-sm mr-2">{referenceNote.description}</p>
              </div>
              <button 
                className="ml-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 whitespace-nowrap"
                onClick={() => handleLinkClick(referenceNote)}
              >
                View
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal for displaying embedded content */}
      {showModal && modalContent && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold text-white">{modalContent.title}</h3>
              <button 
                onClick={closeModal}
                className="text-gray-400 hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4">
              {modalContent.type === 'video' ? (
                <div className="aspect-video">
                  <iframe 
                    src={getYouTubeEmbedUrl(modalContent.url)}
                    title={modalContent.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : modalContent.type === 'book' ? (
                <iframe 
                  src={modalContent.url}
                  title={modalContent.title}
                  className="w-full h-[60vh]"
                  frameBorder="0"
                ></iframe>
              ) : (
                <div className="text-white">
                  <p>{modalContent.description}</p>
                  <a 
                    href={modalContent.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline"
                  >
                    Open link
                  </a>
                </div>
              )}
            </div>
            
            <div className="flex justify-between">
              {/* Show Reference button */}
              <button
                onClick={() => {
                  window.open(modalContent.url, '_blank');
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
              >
                Show Reference
              </button>
              
              <button
                onClick={closeModal}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReferenceOverlay;
